import {api, generateDraft, patchTicket} from '../api';
import {toast} from '../components/ui/sonner';
import {errorMessage} from '../errors';
import type {AIModelId, AIModelSettings, Ticket} from '../types';
import type {ActionContext, WorkspaceActions, WorkspaceState} from './types';

function generationModel(state: WorkspaceState): AIModelId | null {
  if (!state.aiSettings) { toast.error('AI settings are unavailable. Reload the workspace before generating a reply.'); return null; }
  if (state.modelSaving) { toast.error('Please wait for the model preference to finish saving, then generate the reply.'); return null; }
  return state.aiSettings.selected_model_id;
}

export function createModelActions({dispatch}: ActionContext): Pick<WorkspaceActions, 'selectModel'> {
  async function selectModel(modelId: AIModelId): Promise<void> {
    dispatch({type: 'patch', value: {modelSaving: true}});
    try {
      const aiSettings = await api<AIModelSettings>('/ai/preference', {method: 'PATCH', body: JSON.stringify({model_id: modelId})});
      dispatch({type: 'patch', value: {aiSettings}}); toast.success('AI model preference saved');
    } catch (failure: unknown) { toast.error(errorMessage(failure)); }
    finally { dispatch({type: 'patch', value: {modelSaving: false}}); }
  }
  return {selectModel};
}

export function createTicketActions({dispatch, getState}: ActionContext, refresh: () => Promise<void>): Pick<WorkspaceActions, 'update' | 'generate'> {
  async function update(id: string, patch: Partial<Ticket>): Promise<Ticket> {
    const ticket = await patchTicket(id, patch);
    dispatch({type: 'ticket-saved', ticket}); return ticket;
  }
  async function generate(id: string, tone = 'Friendly'): Promise<void> {
    const model = generationModel(getState());
    if (!model) return;
    dispatch({type: 'generation-progress', id, stage: 'Preparing your draft…'});
    try {
      const ticket = await generateDraft(id, tone, stage => dispatch({type: 'generation-progress', id, stage}), model);
      dispatch({type: 'ticket-saved', ticket});
      toast.success(ticket.needs_human ? 'Draft saved — human review needed' : 'Your grounded draft is ready');
    } catch (failure: unknown) {
      toast.error(errorMessage(failure));
      await refresh().catch(error => dispatch({type: 'patch', value: {error: errorMessage(error)}}));
    } finally { dispatch({type: 'generation-finished', id}); }
  }
  return {update, generate};
}